import { failureWords } from '@/shared/failure/failure';

/**
 * What the server said to a doctor's or a patient's screen, sorted into where
 * the screen shows it. The request of 6 October 2026 (second), items 4 and 5.
 *
 * A screen may not import the HTTP client, so the refusal is read by its
 * shape: a status, a message, and the fields a 400 names.
 */

/** The HTTP status a refusal came with, or null for a failure that is not the server's answer. */
export function statusOf(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null;
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : null;
}

/**
 * What to say of a list or a record that could not be read. A refusal is said
 * in the server's own words, which name what the reader may not do; a fault or
 * a lost connection in the plain ones, which is all there is to say of it, and
 * each in its own: see shared/failure.
 */
export function readFailure(error: unknown): string {
  return failureWords(error);
}

/** The first sentence the server gave each field of a 400, by the field's name. */
function fieldErrorsOf(error: unknown): Record<string, string> {
  if (typeof error !== 'object' || error === null || !('fieldErrors' in error)) return {};
  const fields = error.fieldErrors;
  if (typeof fields !== 'object' || fields === null) return {};

  const first: Record<string, string> = {};
  for (const [field, messages] of Object.entries(fields as Record<string, unknown>)) {
    const message: unknown = Array.isArray(messages) ? messages[0] : undefined;
    if (typeof message === 'string' && message !== '') first[field] = message;
  }
  return first;
}

export interface FormRefusal<Field extends string> {
  /** The server's sentence for each of the form's own fields it refused. */
  fields: Partial<Record<Field, string>>;
  /**
   * The server's sentence for the form as a whole: a 409 (a record of the same
   * name, or a copy somebody changed since), a 403 (the person may not do
   * this), or a 400 on nothing the form draws. Null when the fields carry it
   * all, or when it is not the form's to show.
   */
  message: string | null;
}

/**
 * Where a refused save is shown, or null when the form has no place for it and
 * the caller lets it travel on to the toast every refusal gets.
 *
 * - a 409 is the form's own message: no field is wrong, the record is;
 * - a 403 is too: the person may not make this change, in the server's words,
 *   which name what they may not do. The request of 6 October 2026 (third),
 *   item 4: a rename and an edit of the details are two permissions, and a
 *   grant taken away while the form was open is said where the person is
 *   looking;
 * - a 400 goes back on the fields it names, as many as it names;
 * - anything else (no connection, a record deleted meanwhile, a fault at the
 *   server) is nothing the person can correct in the form.
 */
export function formRefusal<Field extends string>(
  error: unknown,
  known: readonly Field[],
): FormRefusal<Field> | null {
  const message = error instanceof Error && error.message !== '' ? error.message : null;
  const status = statusOf(error);
  if (status === 409 || (status === 403 && message !== null)) return { fields: {}, message };

  const answered = fieldErrorsOf(error);
  const fields: Partial<Record<Field, string>> = {};
  for (const field of known) {
    const sentence = answered[field];
    if (sentence !== undefined) fields[field] = sentence;
  }
  if (Object.keys(fields).length > 0) return { fields, message: null };

  // A 400 about something the form does not draw is still about what was sent.
  return statusOf(error) === 400 && message !== null ? { fields: {}, message } : null;
}
